import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SuperAdminLayout } from './SuperAdminLayout';
import {
  Shield, Smartphone, Check, X, Key, Lock, Unlock, Globe, Save
} from 'lucide-react';

const SuperAdminSecurity = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [twoFAStatus, setTwoFAStatus] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [ipRestrictions, setIpRestrictions] = useState('');
  const [savedIP, setSavedIP] = useState(false);

  useEffect(() => {
    fetchStatus();
    fetchConfig();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/auth/2fa/status');
      setTwoFAStatus(res.data.enabled);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchConfig = async () => {
    try {
      const res = await api.get('/super-admin/config');
      setIpRestrictions(res.data.admin_ip_whitelist || '');
    } catch (err) { console.error(err); }
  };

  const handleSetup = async () => {
    try {
      const res = await api.post('/auth/2fa/setup');
      setSetupData(res.data);
    } catch (err) { setMessage(err.response?.data?.detail || t('saSecurity.errorGeneric')); }
  };

  const handleVerify = async () => {
    try {
      await api.post('/auth/2fa/verify', { code: verifyCode });
      setTwoFAStatus(true);
      setSetupData(null);
      setVerifyCode('');
      setMessage(t('saSecurity.twoFAEnabledSuccess'));
    } catch (err) { setMessage(err.response?.data?.detail || t('saSecurity.invalidCode')); }
  };

  const handleDisable = async () => {
    try {
      await api.post('/auth/2fa/disable', { code: disableCode });
      setTwoFAStatus(false);
      setDisableCode('');
      setMessage(t('saSecurity.twoFADisabled'));
    } catch (err) { setMessage(err.response?.data?.detail || t('saSecurity.invalidCode')); }
  };

  const handleSaveIP = async () => {
    try {
      await api.put('/super-admin/config', { admin_ip_whitelist: ipRestrictions });
      setSavedIP(true);
      setTimeout(() => setSavedIP(false), 2000);
    } catch (err) { setMessage(t('saSecurity.saveError')); }
  };

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="space-y-6 max-w-2xl" data-testid="super-admin-security-settings">
        <div>
          <h1 className="text-xl font-bold text-white">{t('saSecurity.pageTitle')}</h1>
          <p className="text-sm text-gray-500">{t('saSecurity.pageSubtitle')}</p>
        </div>

        {message && (
          <div className="p-3 rounded-lg bg-[#009b3a]/10 border border-[#009b3a]/20 text-xs text-[#009b3a]" data-testid="security-message">
            {message}
          </div>
        )}

        {/* 2FA Section */}
        <div className="bg-[#111827] border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-[#009b3a]" />
              <div>
                <h3 className="text-sm font-semibold text-white">{t('saSecurity.twoFATitle')}</h3>
                <p className="text-xs text-gray-500">Google Authenticator / TOTP</p>
              </div>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${twoFAStatus ? 'bg-[#009b3a]/15 text-[#009b3a]' : 'bg-red-500/15 text-red-400'}`}>
              {twoFAStatus ? t('saSecurity.statusEnabled') : t('saSecurity.statusDisabled')}
            </span>
          </div>

          {!twoFAStatus && !setupData && (
            <button onClick={handleSetup} data-testid="setup-2fa-btn" className="flex items-center gap-2 px-4 py-2.5 bg-[#009b3a] text-white rounded-lg text-sm font-medium hover:bg-[#00b344] transition-colors">
              <Lock className="w-4 h-4" /> {t('saSecurity.setupBtn')}
            </button>
          )}

          {setupData && (
            <div className="space-y-4 mt-4 bg-[#0a0f1c] rounded-xl p-4 border border-white/5">
              <p className="text-xs text-gray-400">{t('saSecurity.scanQrInstruction')}</p>
              {setupData.qr_code && <img src={setupData.qr_code} alt={t('saSecurity.qrCodeAlt')} className="mx-auto w-48 h-48 rounded-lg" data-testid="2fa-qr-code" />}
              <div>
                <p className="text-[10px] text-gray-500 mb-1">{t('saSecurity.manualKeyInstruction')}</p>
                <code className="text-xs text-[#fedd00] bg-[#111827] px-3 py-1.5 rounded block text-center break-all">{setupData.secret}</code>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saSecurity.verificationCodeLabel')}</label>
                <div className="flex gap-2">
                  <input type="text" value={verifyCode} onChange={e => setVerifyCode(e.target.value)} placeholder="000000" maxLength={6}
                    className="flex-1 px-3 py-2.5 bg-[#111827] border border-white/10 rounded-lg text-sm text-white text-center tracking-widest focus:outline-none focus:border-[#009b3a]/40"
                    data-testid="2fa-verify-input"
                  />
                  <button onClick={handleVerify} data-testid="2fa-verify-btn" className="px-4 py-2.5 bg-[#009b3a] text-white rounded-lg text-sm font-medium">
                    {t('saSecurity.verifyBtn')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {twoFAStatus && (
            <div className="mt-4 bg-[#0a0f1c] rounded-xl p-4 border border-white/5">
              <p className="text-xs text-gray-400 mb-2">{t('saSecurity.disableInstruction')}</p>
              <div className="flex gap-2">
                <input type="text" value={disableCode} onChange={e => setDisableCode(e.target.value)} placeholder="000000" maxLength={6}
                  className="flex-1 px-3 py-2.5 bg-[#111827] border border-white/10 rounded-lg text-sm text-white text-center tracking-widest focus:outline-none"
                  data-testid="2fa-disable-input"
                />
                <button onClick={handleDisable} data-testid="2fa-disable-btn" className="px-4 py-2.5 bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg text-sm font-medium hover:bg-red-500/30">
                  <Unlock className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* IP Restriction */}
        <div className="bg-[#111827] border border-white/5 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-5 h-5 text-amber-400" />
            <div>
              <h3 className="text-sm font-semibold text-white">{t('saSecurity.ipRestrictionTitle')}</h3>
              <p className="text-xs text-gray-500">{t('saSecurity.ipRestrictionSubtitle')}</p>
            </div>
          </div>
          <textarea
            value={ipRestrictions}
            onChange={e => setIpRestrictions(e.target.value)}
            placeholder={t('saSecurity.ipPlaceholder')}
            rows={3}
            className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-amber-400/40 resize-none font-mono"
            data-testid="ip-restriction-input"
          />
          <button onClick={handleSaveIP} data-testid="save-ip-btn" className="mt-2 flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-lg text-xs font-semibold hover:bg-amber-500/20 transition-all">
            {savedIP ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
            {savedIP ? t('saSecurity.saved') : t('saSecurity.save')}
          </button>
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminSecurity;
